import { useMemo } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useUIStore } from "@/stores/ui-store";
import { usePopulatedCategories } from "@/hooks/use-categories";
import { useProducts } from "@/hooks/use-products";
import { Logo } from "./logo";

export function MobileNav() {
  const open = useUIStore((s) => s.mobileNavOpen);
  const close = useUIStore((s) => s.closeMobileNav);
  const { data: categories = [] } = usePopulatedCategories();
  const { data: products = [] } = useProducts();

  const populatedSlugs = useMemo(
    () => new Set(products.map((p) => p.category)),
    [products],
  );

  const navCategories = useMemo(
    () => categories.filter((c) => c.active && populatedSlugs.has(c.slug)),
    [categories, populatedSlugs],
  );

  const links = [
    { to: "/shop", label: "Shop All" },
    { to: "/about", label: "About Dr. Burns" },
    { to: "/cbd-101", label: "CBD 101" },
    { to: "/faq", label: "FAQ" },
    { to: "/contact", label: "Contact" },
    { to: "/account", label: "My Account" },
  ];

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-night-blue/40 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
          />
          <motion.aside
            className="fixed inset-y-0 left-0 z-50 flex w-[85%] max-w-sm flex-col gap-4 overflow-y-auto bg-white p-5 lg:hidden"
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 280 }}
          >
            <div className="flex items-center justify-between">
              <Logo />
              <button
                type="button"
                onClick={close}
                aria-label="Close menu"
                className="text-night-blue/70"
              >
                <X size={24} />
              </button>
            </div>
            <nav className="flex flex-col">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={close}
                  className="border-b border-night-blue/10 py-3 font-condensed text-base font-bold uppercase tracking-wide text-night-blue"
                >
                  {l.label}
                </Link>
              ))}
            </nav>
            <div>
              <span className="mb-2 block font-condensed text-xs font-bold uppercase tracking-wider text-night-blue/70">
                Shop by Condition
              </span>
              <div className="grid grid-cols-2 gap-2">
                {navCategories.map((c) => (
                  <Link
                    key={c.id}
                    to={`/shop/${c.slug}`}
                    onClick={close}
                    className="rounded-soft bg-medical-grey px-3 py-2 text-sm font-medium text-night-blue"
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
